import { VIEW_FILTER_PARAMETER_TYPE } from '@helpers/constants';
import { ClusterSegment } from '@common-modules/mediaDetails/models/ClusterItem';
import { OverlayTool } from './OverlayTool';

const OverlayPreviewOptions = [
  'redacted',
  'outline',
  'black_fill',
  'none',
] as const;

export type OverlayPreviewOptionsType = (typeof OverlayPreviewOptions)[number];

const AudioPreviewOptions = ['original', 'redacted', 'voice_mask'] as const;

export type AudioPreviewOptionsType = (typeof AudioPreviewOptions)[number];

export function isOverlayPreviewOptionsType(
  preview: string
): preview is OverlayPreviewOptionsType {
  return OverlayPreviewOptions.includes(preview as any);
}

export function isAudioPreviewOptionsType(
  preview: string
): preview is AudioPreviewOptionsType {
  return AudioPreviewOptions.includes(preview as any);
}

export interface HighlightedOverlay {
  readonly id: string;
  readonly timeMs: number;
  readonly groupId: string;
  readonly type: string;
  readonly newSprayPaintEditId?: string | undefined;
}

export interface AudioPreviewOption {
  readonly value: AudioPreviewOptionsType;
  readonly label: 'Original' | 'Redacted' | 'Voice Mask';
}

export interface OverlayPreviewOption {
  readonly value: OverlayPreviewOptionsType;
  readonly label: 'Redacted' | 'Outline' | 'Blackfill' | 'None';
}
export interface FilterParameters {
  readonly show: Record<VIEW_FILTER_PARAMETER_TYPE, boolean>;
  readonly remove: Record<VIEW_FILTER_PARAMETER_TYPE, boolean>;
}

export interface FillBadgeEditAnchor {
  readonly clusterId: string;
  readonly groupId: string;
  readonly objectId: string;
  readonly anchorElement: {
    top: number;
    left: number;
  };
}

export interface ViewSettings {
  readonly playbackSpeed: number; // 0.25 - 10
  readonly playbackDirection: 'forward' | 'reverse';
  // readonly overlayPreview: 'outline' | 'black_fill' | 'none';
  readonly overlayPreview: OverlayPreviewOptionsType;
  readonly overlayTool: OverlayTool;
  readonly audioPreview: AudioPreviewOptionsType;
  readonly selectedPolyGroups: {
    readonly [id: string]: boolean | undefined;
  };
  readonly clusterMergeGroupIds: {
    readonly [id: string]: boolean;
  };
  readonly clusterMergeSegments: {
    readonly [id: string]: ClusterSegment;
  };
  readonly expandedPolyGroups: {
    readonly [id: string]: boolean | undefined;
  };
  readonly sortPolyGroupsBy: {
    readonly column: 'startTimeMs' | 'autoInterp' | 'manualInterp';
    readonly direction: 'asc' | 'desc';
  };
  readonly filterParameters: FilterParameters;
  readonly showGlobalSettingsModal: boolean;

  /**
   * VE-25876 — whether the Faces/Clusters list is scoped to the groups on screen
   * at the playhead. Lives here rather than in the panel's own component state
   * because the right panel swaps components wholesale when the user visits the
   * Audio or Comments tab, which would otherwise drop the preference. Being part
   * of this slice, it resets on ROUTE_MEDIA_DETAILS — so it survives a tab
   * round-trip but not opening another TDO, which is the intended lifetime.
   */
  readonly onscreenOnlyFilter: boolean;

  /**
   * Whether the operator has made a choice about `onscreenOnlyFilter`, either
   * way (VE-25876).
   *
   * The scope engages itself the first time a TDO is played, on the grounds that
   * the full list is unscannable while video runs. That is a one-time offer, not
   * a standing behaviour: once this is set, nothing engages the scope but the
   * operator. Turning it off therefore keeps it off for this TDO rather than
   * having it come back on the next time they press play.
   *
   * One flag covers every path. Auto-on then off leaves it off; a manual toggle
   * before playback makes the auto-engage a no-op; never touching it leaves it
   * on. Resets with the rest of the slice when another TDO is opened.
   */
  readonly onscreenOnlyFilterUserSet: boolean;

  readonly highlightedOverlay?: HighlightedOverlay;
  readonly prevMarkerTime: number;
  readonly draggedOverlayId: string | null;
  readonly fillBadgeEditAnchor: FillBadgeEditAnchor | null;
}
